import { ActionInterface, ActionTakenType, MultiSelectPayload, createActionTaken } from "@swivl-mono/types";
import React, { Component } from "react";

import { Config } from "../../../../Config/Config";
import { LanguageContext, t } from "../../../../i18n/strings";
import { MessageHandlerInterface } from "../../../../messages/MessageHandler";
import { ListItemData } from "../../ListItem";
import "./MultiSelect.scss";

interface MultiSelectOptionProps {
  isSelectable: boolean;
  action: ActionInterface;
  actionSelected(item: ActionInterface): void;
  selectedActionIds: Array<string>;
}

class MultiSelectOption extends React.Component<MultiSelectOptionProps> {
  constructor(props: MultiSelectOptionProps) {
    super(props);
    this.onClick = this.onClick.bind(this);
  }
  onClick() {
    if (!Config.canPressButtons) {
      return;
    }

    this.props.actionSelected(this.props.action);
  }
  render() {
    const selected = this.props.selectedActionIds.includes(this.props.action.id);
    const onClick = this.props.isSelectable ? this.onClick : null;
    let className = "scc-multiSelectButton swivl-svgButton";

    if (this.props.isSelectable) {
      className += " swivl-primaryButton isSelectable";

      if (selected) {
        className += " swivl-secondaryButton active";
      }
    } else {
      if (selected) {
        className += " swivl-secondaryButton active";
      } else {
        className += " swivl-userBubble";
      }
    }

    let checkMark = !selected ? null : (
      <svg style={{ width: "15x", height: "11px" }}>
        <polygon
          id="Check"
          fillRule="nonzero"
          points="3.06066017 3.92977245 0.939339828 6.0510928 5.77480322 10.8865562 14.6006992 2.06066017 12.4793789 -0.0606601718 5.77480322 6.6439155"
        ></polygon>
      </svg>
    );

    let checkBox = (
      <div className="scc-multiSelectButtonCheckBoxWrapper">
        <div className="scc-multiSelectButtonCheckBox">{checkMark}</div>
      </div>
    );
    return (
      <div className={className} onClick={onClick as any}>
        {checkBox}
        {this.props.action.text}
      </div>
    );
  }
}

export interface MultiSelectProps {
  item: ListItemData;
  messageHandler: MessageHandlerInterface;
}
export interface MultiSelectState {
  selectedActionIds: Array<string>;
}

export class MultiSelect extends React.Component<MultiSelectProps, MultiSelectState> {
  static contextType = LanguageContext;
  declare context: React.ContextType<typeof LanguageContext>;

  constructor(props: MultiSelectProps) {
    super(props);
    this.state = { selectedActionIds: [] };

    this.actionSelected = this.actionSelected.bind(this);
    this.submitClicked = this.submitClicked.bind(this);
  }

  actionSelected(action: ActionInterface) {
    if (!Config.canPressButtons) {
      return;
    }
    let selectedActionIds = this.state.selectedActionIds;

    if (selectedActionIds.includes(action.id)) {
      selectedActionIds = selectedActionIds.filter((id) => id !== action.id);
    } else {
      selectedActionIds = [...selectedActionIds, action.id];
    }
    this.setState({ selectedActionIds });
  }

  // Resolve the canonical (untranslated) action.text for a given id. When the message went
  // through MessageTranslationService the original English texts live on translation.originalPayload;
  // otherwise the live payload is already canonical.
  private originalActionTextById(id: string): string | undefined {
    const message = this.props.item.message;
    const originalActions = (message.translation?.originalPayload as MultiSelectPayload | undefined)?.actions;
    const liveActions = (message.payload as MultiSelectPayload).actions;
    const original = originalActions?.find((a) => a.id === id);
    if (original?.text) return original.text;
    return liveActions.find((a) => a.id === id)?.text;
  }

  submitClicked() {
    if (!Config.canPressButtons) {
      return;
    }
    const messageId = this.props.item.message.uuid;
    const canonicalTexts = this.state.selectedActionIds.map((id) => this.originalActionTextById(id) ?? "").filter((s) => s.length > 0);
    const actionTaken = createActionTaken(ActionTakenType.Selected, canonicalTexts.join(","));

    this.props.messageHandler.actionTaken(messageId, actionTaken);
  }

  render() {
    const item: any = this.props.item;
    const actions: Array<ActionInterface> = item.message?.payload.actions;

    const actionTaken = this.props.item.message.actionTaken;
    let selectedActionIds = this.state.selectedActionIds;
    let canSelect = true;
    if (actionTaken) {
      if (actionTaken.type == ActionTakenType.Selected && actionTaken.target && typeof actionTaken.target === "string") {
        // Persisted target stores canonical (English) texts; map back to ids for highlight state.
        const selectedTexts = actionTaken.target.split(",");
        selectedActionIds = actions
          .filter((a) => {
            const canonical = this.originalActionTextById(a.id);
            return canonical ? selectedTexts.includes(canonical) : selectedTexts.includes(a.text);
          })
          .map((a) => a.id);
        canSelect = false;
      }
    }

    const buttons = actions.map((item, _) => {
      return (
        <MultiSelectOption
          key={item.id}
          action={item}
          actionSelected={this.actionSelected}
          selectedActionIds={selectedActionIds}
          isSelectable={canSelect}
        />
      );
    });

    const saveButton = canSelect ? (
      <div className="scc-multiSelectSaveButton swivl-primaryButton" onClick={this.submitClicked}>
        {t("multiSelectSubmit", this.context)}
      </div>
    ) : null;

    return (
      <div className="scc-messageGroup">
        <div className="scc-multiSelectActions">
          {buttons}
          {saveButton}
        </div>
      </div>
    );
  }
}
